import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export const BlogArticle = ({ title, date, category, content, slug }) => {
  return (
    <div className="min-h-screen pt-20">
      <article className="py-20 md:py-32" data-testid={`blog-${slug}`}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/blog"
            className="inline-flex items-center text-primary hover:text-[#1a5c45] font-medium mb-8"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center space-x-4 mb-6">
              <span className="inline-block px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full">
                {category}
              </span>
              <span className="flex items-center text-sm text-stone-500">
                <Calendar className="w-4 h-4 mr-1" />
                {date}
              </span>
              <span className="flex items-center text-sm text-stone-500">
                <User className="w-4 h-4 mr-1" />
                Amigos Solar Team
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-heading font-semibold text-primary tracking-tight leading-[1.1] mb-8">
              {title}
            </h1>

            <div className="prose prose-lg max-w-none">
              {content}
            </div>

            <div className="mt-16 p-8 bg-gradient-to-br from-lime-50 to-emerald-50 rounded-3xl border border-lime-200">
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">
                Ready to Go Solar?
              </h3>
              <p className="text-stone-600 mb-6">
                Get a free consultation and custom proposal for your property.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-secondary text-white hover:bg-gradient-to-r hover:from-secondary hover:to-accent rounded-full px-8 py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                data-testid="blog-cta-button"
              >
                Get Free Solar Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </article>
    </div>
  );
};

// Individual blog articles

export const Solar101 = () => {
  const content = (
    <>
      <p>
        Solar energy is revolutionizing how Indian homes and businesses consume electricity. This comprehensive guide will walk you through everything you need to know about rooftop solar systems in India.
      </p>

      <h2>How Do Solar Panels Work?</h2>
      <p>
        Solar panels convert sunlight directly into electricity using photovoltaic (PV) cells. When sunlight hits these cells, electrons are knocked loose, creating an electric current. This direct current (DC) is then converted to alternating current (AC) by an inverter, making it usable for your home appliances.
      </p>

      <h2>Key Components of a Solar System</h2>
      <h3>1. Solar Panels</h3>
      <p>
        The heart of your system. Modern panels come in three types: monocrystalline (most efficient, 18-22%), polycrystalline (cost-effective, 15-17%), and thin-film (flexible but lower efficiency). For Indian conditions, monocrystalline panels are recommended for their superior heat tolerance.
      </p>

      <h3>2. Inverter</h3>
      <p>
        Converts DC power from panels to AC power for your home. String inverters are common for residential installations, while microinverters offer panel-level optimization for complex roof layouts.
      </p>

      <h3>3. Mounting Structure</h3>
      <p>
        Must withstand wind speeds up to 150 km/h and last 25+ years. Hot-dipped galvanized steel or aluminum structures are standard in India.
      </p>

      <h2>Net Metering: Sell Back to the Grid</h2>
      <p>
        Net metering allows you to export excess solar generation to the grid and receive credits on your electricity bill. When your system produces more power than you consume (typically during peak sun hours), the excess is fed back to the grid. At night or on cloudy days, you draw power from the grid. The difference is adjusted in your monthly bill.
      </p>

      <h2>Government Policies and Subsidies</h2>
      <p>
        Under the PM Surya Ghar Muft Bijli Yojana, residential installations get:
      </p>
      <ul>
        <li>₹18,000/kW for systems up to 3 kW</li>
        <li>₹9,000/kW for additional capacity (3-10 kW)</li>
        <li>Maximum subsidy of ₹78,000 for a 10 kW system</li>
      </ul>

      <h2>ROI and Payback Period</h2>
      <p>
        With subsidies, a typical 3 kW residential system costs ₹1.5-2 lakhs and pays back in 3-4 years. Over 25 years, you can save ₹10-15 lakhs in electricity bills. Commercial systems without subsidies have a payback period of 4-5 years.
      </p>

      <h2>Is Solar Right for You?</h2>
      <p>
        Consider solar if you:
      </p>
      <ul>
        <li>Have unshaded roof space (at least 100 sq ft for 1 kW)</li>
        <li>Pay monthly electricity bills of ₹2,000 or more</li>
        <li>Own the property or have landlord permission</li>
        <li>Plan to stay at the location for 5+ years</li>
      </ul>
    </>
  );

  return (
    <BlogArticle
      title="Solar 101: How Rooftop Solar Works in India"
      date="December 15, 2024"
      category="Guide"
      content={content}
      slug="solar-101-how-rooftop-solar-works"
    />
  );
};

export const OnGridVsOffGrid = () => {
  const content = (
    <>
      <p>
        Choosing between on-grid, off-grid, and hybrid solar systems can be confusing. This guide breaks down each option to help you make the right choice.
      </p>

      <h2>On-Grid Solar Systems</h2>
      <h3>How It Works</h3>
      <p>
        Connected directly to the electricity grid via net metering. Excess power is exported to the grid, earning you credits. You draw from the grid when solar isn't producing enough.
      </p>

      <h3>Pros</h3>
      <ul>
        <li>Lowest cost per watt (₹55,000-65,000 per kW)</li>
        <li>Best ROI with fastest payback (3-5 years)</li>
        <li>No battery maintenance required</li>
        <li>Eligible for government subsidies</li>
      </ul>

      <h3>Cons</h3>
      <ul>
        <li>No power during grid outages (safety requirement)</li>
        <li>Dependent on DISCOM for net metering approval</li>
      </ul>

      <h3>Best For</h3>
      <p>
        Areas with stable grid supply and minimal power cuts. Ideal for most urban homes and commercial establishments.
      </p>

      <h2>Off-Grid Solar Systems</h2>
      <h3>How It Works</h3>
      <p>
        Completely independent from the grid. Power is stored in batteries for use during night and cloudy periods.
      </p>

      <h3>Pros</h3>
      <ul>
        <li>Complete energy independence</li>
        <li>No grid dependency or electricity bills</li>
        <li>Perfect for remote areas without grid access</li>
      </ul>

      <h3>Cons</h3>
      <ul>
        <li>Highest upfront cost (₹90,000-1,10,000 per kW with batteries)</li>
        <li>Batteries need replacement every 5-10 years</li>
        <li>System must be sized for worst-case scenarios</li>
        <li>Not eligible for government subsidies</li>
      </ul>

      <h3>Best For</h3>
      <p>
        Remote farmhouses, mountain resorts, or areas with frequent 6+ hour power cuts.
      </p>

      <h2>Hybrid Solar Systems</h2>
      <h3>How It Works</h3>
      <p>
        Combines benefits of both. Connected to grid with battery backup. System prioritizes solar, then batteries, then grid.
      </p>

      <h3>Pros</h3>
      <ul>
        <li>Backup power during outages</li>
        <li>Can use grid as backup instead of oversizing batteries</li>
        <li>Flexibility to go fully off-grid later</li>
      </ul>

      <h3>Cons</h3>
      <ul>
        <li>Higher cost than on-grid (₹70,000-85,000 per kW)</li>
        <li>Battery maintenance required</li>
        <li>Complex system with more components</li>
      </ul>

      <h3>Best For</h3>
      <p>
        Areas with frequent 2-4 hour power cuts. Homes with critical loads like medical equipment or home offices.
      </p>

      <h2>Making the Right Choice</h2>
      <p>
        Choose <strong>On-Grid</strong> if: You have reliable grid supply, want maximum savings, and power cuts are rare.
      </p>
      <p>
        Choose <strong>Off-Grid</strong> if: No grid access or daily 6+ hour cuts, and you want complete independence.
      </p>
      <p>
        Choose <strong>Hybrid</strong> if: You need backup for occasional power cuts but want the benefits of grid connection.
      </p>
    </>
  );

  return (
    <BlogArticle
      title="On-Grid vs Off-Grid vs Hybrid: Which Solar System Is Right for You?"
      date="December 10, 2024"
      category="Comparison"
      content={content}
      slug="on-grid-vs-off-grid-vs-hybrid"
    />
  );
};

export const GovernmentSubsidies = () => {
  const content = (
    <>
      <p>
        The Government of India offers substantial subsidies to make solar energy affordable for every household. Here's everything you need to know about claiming your benefits.
      </p>

      <h2>PM Surya Ghar Muft Bijli Yojana</h2>
      <p>
        Launched in 2024, this scheme aims to install rooftop solar on 1 crore households, providing free electricity up to 300 units per month.
      </p>

      <h3>Subsidy Structure</h3>
      <ul>
        <li><strong>1-3 kW systems:</strong> ₹18,000 per kW (Total: ₹54,000 for 3 kW)</li>
        <li><strong>Above 3 kW:</strong> ₹18,000 per kW for first 3 kW + ₹9,000 per kW for additional capacity</li>
        <li><strong>Example:</strong> For a 5 kW system: (3 × ₹18,000) + (2 × ₹9,000) = ₹72,000</li>
      </ul>

      <h2>Eligibility Criteria</h2>
      <ul>
        <li>Must be an individual residential consumer</li>
        <li>Property should be owned or you must have landlord consent</li>
        <li>Electricity connection in your name</li>
        <li>System size limited to sanctioned load or 10 kW, whichever is lower</li>
        <li>Must use MNRE-approved panels and inverters</li>
      </ul>

      <h2>How to Apply</h2>
      <h3>Step 1: Register on National Portal</h3>
      <p>
        Visit the PM Surya Ghar portal and register with your electricity consumer number and Aadhaar.
      </p>

      <h3>Step 2: Choose Vendor</h3>
      <p>
        Select from MNRE-empaneled vendors. Ensure they are registered on the portal to avoid subsidy rejections.
      </p>

      <h3>Step 3: Technical Feasibility</h3>
      <p>
        DISCOM conducts a technical feasibility check. They'll inspect your roof and electrical setup.
      </p>

      <h3>Step 4: Installation</h3>
      <p>
        Vendor installs the system. Ensure they follow all MNRE guidelines for panel mounting and wiring.
      </p>

      <h3>Step 5: Net Meter Installation</h3>
      <p>
        DISCOM installs a bi-directional meter. This usually takes 2-4 weeks post-installation.
      </p>

      <h3>Step 6: Commissioning & Subsidy</h3>
      <p>
        Submit commissioning report on the portal with photos and documents. Subsidy is transferred to your bank account within 30 days.
      </p>

      <h2>Required Documents</h2>
      <ul>
        <li>Aadhaar Card</li>
        <li>PAN Card</li>
        <li>Electricity Bill (last 3 months)</li>
        <li>Property ownership proof or NOC from landlord</li>
        <li>Bank account details (passbook/cancelled cheque)</li>
        <li>Roof space availability certificate</li>
      </ul>

      <h2>State-Specific Incentives</h2>
      <p>
        Several states offer additional benefits:
      </p>
      <ul>
        <li><strong>Gujarat:</strong> Accelerated depreciation benefits for commercial units</li>
        <li><strong>Maharashtra:</strong> Electricity duty waiver and net metering incentives</li>
        <li><strong>Karnataka:</strong> Expedited approvals within 15 days</li>
        <li><strong>Tamil Nadu:</strong> Banking facility for excess generation</li>
      </ul>

      <h2>Common Mistakes to Avoid</h2>
      <ul>
        <li>Choosing non-MNRE approved components</li>
        <li>Working with unregistered vendors</li>
        <li>Incorrect system sizing exceeding sanctioned load</li>
        <li>Incomplete documentation during application</li>
        <li>Not following up on net meter installation</li>
      </ul>
    </>
  );

  return (
    <BlogArticle
      title="Government Solar Subsidies Explained for Homeowners"
      date="December 5, 2024"
      category="Finance"
      content={content}
      slug="government-solar-subsidies"
    />
  );
};

export const MistakesToAvoid = () => {
  const content = (
    <>
      <p>
        Installing solar panels is a significant investment. Avoid these common mistakes to ensure maximum savings and system performance.
      </p>

      <h2>1. Not Researching Your Installer</h2>
      <p>
        <strong>The Mistake:</strong> Choosing the cheapest quote without verifying credentials, past installations, or customer reviews.
      </p>
      <p>
        <strong>Why It Matters:</strong> Poor installation can lead to water leakage, suboptimal generation, and voided warranties. Fly-by-night operators disappear when you need support.
      </p>
      <p>
        <strong>How to Avoid:</strong> Check for MNRE empanelment, visit past installations, read Google reviews, and ask for customer references. A reputable installer will readily provide these.
      </p>

      <h2>2. Ignoring Roof Condition</h2>
      <p>
        <strong>The Mistake:</strong> Installing solar on a damaged or weak roof without repairs.
      </p>
      <p>
        <strong>Why It Matters:</strong> Solar panels last 25 years. If your roof needs repairs in 5 years, removing and reinstalling panels costs ₹30,000-50,000.
      </p>
      <p>
        <strong>How to Avoid:</strong> Get a structural engineer's assessment. If your roof has cracks or waterproofing issues, fix them before installation.
      </p>

      <h2>3. Oversizing or Undersizing Your System</h2>
      <p>
        <strong>The Mistake:</strong> Installing a system based on available space rather than actual electricity consumption.
      </p>
      <p>
        <strong>Why It Matters:</strong> Oversized systems waste money on unused capacity. Undersized systems won't cover your bills.
      </p>
      <p>
        <strong>How to Avoid:</strong> Analyze your last 12 months of electricity bills. Consider future load additions (EVs, ACs). A good rule: 1 kW generates ~120-150 units/month in India.
      </p>

      <h2>4. Skipping Shade Analysis</h2>
      <p>
        <strong>The Mistake:</strong> Not accounting for trees, buildings, or other obstructions that cast shadows on panels.
      </p>
      <p>
        <strong>Why It Matters:</strong> Even 10% shading can reduce output by 40-50% due to series connection of cells. Nearby under-construction buildings can become permanent obstacles.
      </p>
      <p>
        <strong>How to Avoid:</strong> Conduct a professional shade analysis. Consider future growth of trees. Use microinverters or optimizers if partial shading is unavoidable.
      </p>

      <h2>5. Neglecting Maintenance and Monitoring</h2>
      <p>
        <strong>The Mistake:</strong> Installing solar and forgetting about it. No cleaning schedule or performance monitoring.
      </p>
      <p>
        <strong>Why It Matters:</strong> Dust accumulation can reduce output by 15-30% in Indian conditions. Without monitoring, you won't notice inverter failures or panel degradation.
      </p>
      <p>
        <strong>How to Avoid:</strong> Clean panels every 2-3 months (monthly in dusty areas). Install a monitoring system to track daily generation. Address any 10%+ drop in output immediately.
      </p>

      <h2>Bonus Tips for Success</h2>
      <ul>
        <li><strong>Orientation matters:</strong> South-facing is ideal in India. East-west mounting is acceptable with minor efficiency loss.</li>
        <li><strong>Tilt angle:</strong> Should equal your latitude (±5°). For Mumbai: 19°, Delhi: 28°, Bengaluru: 13°.</li>
        <li><strong>Insurance:</strong> Get solar equipment insurance. Covers fire, theft, and natural disasters. Costs ₹2,000-5,000 annually.</li>
        <li><strong>Warranty clarity:</strong> Panel warranty is 25 years for performance (typically 80% output). Inverter: 5 years. Workmanship: 5 years. Get everything in writing.</li>
        <li><strong>Post-installation:</strong> Keep all commissioning documents, subsidy certificates, and net metering agreements safely. You'll need them for future reference.</li>
      </ul>
    </>
  );

  return (
    <BlogArticle
      title="5 Mistakes to Avoid Before Installing Solar Panels"
      date="November 28, 2024"
      category="Tips"
      content={content}
      slug="5-mistakes-to-avoid"
    />
  );
};
